import { Link, useLocation } from 'react-router-dom';
import type { AdminSession } from '../hooks/useAdminAuth';

interface Props {
  session: AdminSession;
  onLogout: () => void;
  collapsed: boolean;
  onToggle: () => void;
}

const navItems = [
  { path: '/admin', icon: 'ri-dashboard-line', label: '控制台', exact: true, permission: 'dashboard' },
  { path: '/admin/customers', icon: 'ri-user-heart-line', label: '客户管理', permission: 'customers' },
  { path: '/admin/articles', icon: 'ri-article-line', label: '文章管理', permission: 'articles' },
  { path: '/admin/cases', icon: 'ri-heart-line', label: '案例管理', permission: 'cases' },
  { path: '/admin/faqs', icon: 'ri-questionnaire-line', label: 'FAQ管理', permission: 'faqs' },
  { path: '/admin/settings', icon: 'ri-settings-3-line', label: '系统设置', permission: 'settings' },
];

export default function AdminSidebar({ session, onLogout, collapsed, onToggle }: Props) {
  const { pathname } = useLocation();

  const allowedItems = navItems.filter((item) => session.permissions?.includes(item.permission) ?? true);

  const isActive = (item: typeof navItems[0]) => {
    if (item.exact) return pathname === item.path;
    return pathname.startsWith(item.path);
  };

  return (
    <aside
      className={`${collapsed ? 'w-16' : 'w-56'} transition-all duration-300 bg-[#1a2f4a] h-screen sticky top-0 self-start flex flex-col shrink-0`}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10 shrink-0">
        <div className="w-8 h-8 flex items-center justify-center bg-[#c9a96e] rounded-lg shrink-0">
          <i className="ri-shield-keyhole-line text-white text-sm"></i>
        </div>
        {!collapsed && (
          <div>
            <div className="font-bold text-white text-sm">RTB CMS</div>
            <div className="text-white/40 text-xs">管理后台</div>
          </div>
        )}
        <button
          onClick={onToggle}
          className="ml-auto w-6 h-6 flex items-center justify-center text-white/50 hover:text-white cursor-pointer"
        >
          <i className={`ri-${collapsed ? 'menu-unfold' : 'menu-fold'}-line text-sm`}></i>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {allowedItems.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.path}
              to={item.path}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
                active
                  ? 'bg-[#c9a96e] text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-base`}></i>
              </div>
              {!collapsed && <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User info - fixed at bottom */}
      <div className="border-t border-white/10 p-3 shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 flex items-center justify-center bg-[#c9a96e]/20 rounded-full shrink-0">
            <i className="ri-user-line text-[#c9a96e] text-xs"></i>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-white text-xs font-medium truncate">{session.display_name}</div>
              <div className="text-white/40 text-xs">{session.role_display_name || session.role}</div>
            </div>
          )}
        </div>
        <button
          onClick={onLogout}
          title={collapsed ? '退出登录' : undefined}
          className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-white/50 hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer"
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-logout-box-line text-sm"></i>
          </div>
          {!collapsed && <span className="text-xs">退出登录</span>}
        </button>
      </div>
    </aside>
  );
}